import React, { forwardRef, useImperativeHandle, useRef } from 'react'

import { EventEmitterRegistryController } from '@ambire-common/controllers/eventEmitterRegistry/eventEmitterRegistry'
import { parse } from '@ambire-common/libs/richJson/richJson'
import { AutoLockController } from '../../../mobile-web/services/autoLock/autoLockController'
import {
  resolveRailgunWalletResult,
  setRailgunWorkerEngineError,
  setRailgunWorkerEngineReady
} from '../../../mobile-web/services/railgunWorker/railgunWorkerBridge'
import { emitRailgunEvent, type RailgunEventName } from '@web/utils/railgunListener'
import eventBus from '@common/services/event/eventBus'
import TrezorPageController from '../../../mobile-web/services/hardwareWallet/trezorPageController'
import LatticePageController from '../../../mobile-web/services/hardwareWallet/latticePageController'
import LedgerPageController from '../../../mobile-web/services/hardwareWallet/ledgerPageController'

// Phase 4 (keystore isolation): the page no longer constructs `MainController`.
// It runs inside a dedicated Web Worker (see
// `src/mobile-web/services/mainWorker/mainController.worker.ts`) so the decrypted
// keystore `#mainKey` and any in-memory seed never live in the page context.
// This component is now a thin proxy:
//   • spawns the worker and forwards the `init`/`dispatch` ref API to it,
//   • maps worker `reactEvent` messages back onto the shared in-page `eventBus`
//     (the same events the in-page controllers used to emit directly),
//   • keeps `AutoLockController` ON THE PAGE — it needs DOM activity/visibility
//     listeners and the in-page `railgunEmitter`, none of which exist in a
//     Worker — and reaches the worker only to trigger `lock()`.
//
// Known residual gap (plan Phase 4 step 4, "migrate gradually"): RAILGUN and the
// reveal-seed Settings screen still pull the mnemonic back into the page via
// `KEYSTORE_CONTROLLER_SEND_SEED_TO_UI`. That path is unchanged here; isolating
// it requires either gating that message behind a user-interaction check or
// moving RAILGUN's mnemonic-derivation into the worker. The EVM signing/export
// path (`signMessage`, `signAccountOp`, key export) keeps the key in the worker.

export interface WebViewWorkerRef {
  dispatch: (action: any) => void
  init: (config: any) => Promise<string[]>
}

type EventDispatcher = (type: string, payload: any) => void

// In-page event dispatcher. Worker controller updates arrive as `reactEvent`
// messages and are replayed here onto the shared eventBus; the page-side
// AutoLockController emits through the same path. Identical event names to the
// pre-Phase-4 in-page version so `ControllerStoreProvider` is unchanged.
const sendToReactEvent: EventDispatcher = (type, payload) => {
  switch (type) {
    case 'ctrl.update':
      eventBus.emit('ctrlUpdate', {
        ctrlName: payload.ctrlName,
        ctrlState: payload.state,
        forceEmit: payload.forceEmit
      })
      break
    case 'ctrl.error':
      eventBus.emit('error', { errors: payload.errors, controller: payload.ctrlName })
      break
    case 'action.addToast':
      eventBus.emit('addToast', payload)
      break
    case 'action.receiveOneTimeData':
      eventBus.emit('receiveOneTimeData', payload)
      break
    case 'ctrl.debug':
      // eslint-disable-next-line no-console
      console.log(payload.log)
      break
    default:
      break
  }
}

const PAGE_CTRL = 'AutoLockController'

const isAutoLockAction = (action: any): boolean =>
  action?.type === 'AUTO_LOCK_CONTROLLER_SET_AUTO_LOCK_TIME' ||
  action?.type === 'AUTO_LOCK_CONTROLLER_SET_LAST_ACTIVE_TIME' ||
  (action?.type === 'method' && action?.params?.ctrlName === PAGE_CTRL)

export const WebViewWorker = forwardRef<WebViewWorkerRef, {}>((_, ref) => {
  const workerRef = useRef<Worker | null>(null)
  const workerReadyRef = useRef(false)
  // Actions dispatched before the worker finishes booting (e.g. an early
  // UPDATE_UI_VIEW_ROUTE) are buffered, not dropped.
  const pendingRef = useRef<any[]>([])
  const autoLockCtrlRef = useRef<AutoLockController | null>(null)
  const pageRegistryRef = useRef<EventEmitterRegistryController | null>(null)
  // Trezor's real SDK needs the DOM/popup, so it lives on the page. Created
  // lazily on the first device call the worker forwards (`hwTrezorInvoke`), so
  // the connect.trezor.io iframe only loads when the user actually uses Trezor.
  const trezorPageCtrlRef = useRef<TrezorPageController | null>(null)
  // Lattice/GridPlus real SDK lives on the page too (DOM + manual pairing
  // prompts). Created lazily on the first device call the worker forwards
  // (`hwLatticeInvoke`).
  const latticePageCtrlRef = useRef<LatticePageController | null>(null)
  // Ledger speaks WebHID, which does not exist in a Worker, so the device SDK
  // lives on the page too. Created lazily on the first device call the worker
  // forwards (`hwLedgerInvoke`) — constructing it does not touch the device, the
  // HID permission prompt still comes from the user-gesture path in `useLedger`.
  const ledgerPageCtrlRef = useRef<LedgerPageController | null>(null)

  const forwardToWorker = (action: any) => {
    if (workerReadyRef.current && workerRef.current) {
      workerRef.current.postMessage({ type: 'dispatch', action })
    } else {
      pendingRef.current.push(action)
    }
  }

  // Apply the page-owned controllers (AutoLock) without round-tripping to the
  // worker. AUTO_LOCK_CONTROLLER_* actions and AutoLock's INIT/method actions
  // are handled here; everything else is the worker's.
  const handlePageAction = (action: any) => {
    const autoLock = autoLockCtrlRef.current
    switch (action?.type) {
      case 'AUTO_LOCK_CONTROLLER_SET_AUTO_LOCK_TIME':
        if (autoLock) autoLock.autoLockTime = action.params
        break
      case 'AUTO_LOCK_CONTROLLER_SET_LAST_ACTIVE_TIME':
        if (autoLock) autoLock.setLastActiveTime()
        break
      case 'method': {
        const ctrl = autoLock as any
        const { method, args } = action.params || {}
        if (ctrl && typeof ctrl[method] === 'function') ctrl[method](...(args || []))
        break
      }
      default:
        break
    }
  }

  const emitPageCtrlState = () => {
    const autoLock = autoLockCtrlRef.current
    sendToReactEvent('ctrl.update', {
      ctrlName: PAGE_CTRL,
      state: autoLock ? autoLock.toJSON() : null,
      forceEmit: true
    })
  }

  // Run a Trezor device call against the page-side SDK and post the result
  // back to the worker's proxy. Only non-secret data crosses (derived address,
  // unsigned payload, signature); the keystore key never leaves the worker.
  const handleHwTrezorInvoke = async (worker: Worker, msg: any) => {
    const { requestId, method, args = [] } = msg
    try {
      if (!trezorPageCtrlRef.current) trezorPageCtrlRef.current = new TrezorPageController()
      const ctrl = trezorPageCtrlRef.current
      await ctrl.initialLoadPromise

      let value: any
      if (method === 'unlock') {
        value = await ctrl.unlock(args[0], args[1])
      } else if (method === 'signingCleanup') {
        value = await ctrl.signingCleanup()
      } else if (method.startsWith('sdk.')) {
        const fn = method.slice('sdk.'.length)
        const sdk = ctrl.walletSDK as any
        if (typeof sdk[fn] !== 'function') throw new Error(`Unknown Trezor SDK method: ${fn}`)
        value = await sdk[fn](...args)
      } else {
        throw new Error(`Unknown Trezor proxy method: ${method}`)
      }
      worker.postMessage({ type: 'hwTrezorResult', requestId, ok: true, value })
    } catch (error: any) {
      worker.postMessage({
        type: 'hwTrezorResult',
        requestId,
        ok: false,
        error: error?.message || 'Could not unlock the Trezor device. Please try again.'
      })
    }
  }

  // Run a Lattice/GridPlus device call against the page-side gridplus-sdk client
  // and post the result back to the worker's proxy. Only non-secret data crosses
  // (derived address, unsigned payload, signature); the keystore key never leaves
  // the worker.
  const handleHwLatticeInvoke = async (worker: Worker, msg: any) => {
    const { requestId, method, args = [] } = msg
    try {
      if (!latticePageCtrlRef.current) latticePageCtrlRef.current = new LatticePageController()
      const ctrl = latticePageCtrlRef.current as any

      if (typeof ctrl[method] !== 'function') {
        throw new Error(`Unknown Lattice proxy method: ${method}`)
      }
      const value = await ctrl[method](...args)
      worker.postMessage({ type: 'hwLatticeResult', requestId, ok: true, value })
    } catch (error: any) {
      worker.postMessage({
        type: 'hwLatticeResult',
        requestId,
        ok: false,
        error: error?.message || 'Could not unlock the Lattice1 device. Please try again.'
      })
    }
  }

  // Run a Ledger device call against the page-side WebHID controller and post
  // the result back to the worker's proxy. Only non-secret data crosses (derived
  // address, unsigned payload, signature); the keystore key never leaves the
  // worker, and every device operation is confirmed physically on the Ledger.
  const handleHwLedgerInvoke = async (worker: Worker, msg: any) => {
    const { requestId, method, args = [] } = msg
    try {
      if (!ledgerPageCtrlRef.current) ledgerPageCtrlRef.current = new LedgerPageController()
      const ctrl = ledgerPageCtrlRef.current as any

      if (typeof ctrl[method] !== 'function') {
        throw new Error(`Unknown Ledger proxy method: ${method}`)
      }
      const value = await ctrl[method](...args)
      worker.postMessage({ type: 'hwLedgerResult', requestId, ok: true, value })
    } catch (error: any) {
      worker.postMessage({
        type: 'hwLedgerResult',
        requestId,
        ok: false,
        // `LedgerController` already runs its messages through
        // `normalizeLedgerMessage`, so this is the user-facing text.
        error: error?.message || 'Could not connect to your Ledger device. Please try again.',
        // `Error` subclasses do not survive structured clone; flag the class so
        // the worker proxy can rebuild an `ExternalSignerError` (AccountPicker
        // only surfaces `e.message` verbatim for that class).
        isExternalSignerError: error?.name === 'ExternalSignerError',
        sendCrashReport: !!error?.sendCrashReport
      })
    }
  }

  useImperativeHandle(ref, () => ({
    dispatch: (action: any) => {
      // Split-brain routing: AutoLock lives on the page, MainController in the
      // worker. INIT actions enumerate controllers across both, so handle the
      // page controller locally and forward the rest with it stripped out (the
      // worker registry has no AutoLock and would emit a null state for it,
      // clobbering the real value in the store).
      if (action?.type === 'INIT_ALL_CONTROLLERS') {
        emitPageCtrlState()
        const controllers = (action.params?.controllers || []).filter((n: string) => n !== PAGE_CTRL)
        forwardToWorker({ ...action, params: { ...action.params, controllers } })
        return
      }
      if (action?.type === 'INIT_CONTROLLER_STATE') {
        if (action.params?.controller === PAGE_CTRL) emitPageCtrlState()
        else forwardToWorker(action)
        return
      }
      if (isAutoLockAction(action)) {
        handlePageAction(action)
        return
      }
      forwardToWorker(action)
    },
    init: async (config: any) => {
      // eslint-disable-next-line no-console
      console.log('[WebViewWorker.web] init start, config=', config)

      // Page-side registry feeds AutoLock updates into the same eventBus path
      // the worker uses, so the Settings dropdown subscription is identical
      // whether the controller lives on the page or in the worker.
      const pageRegistry = new EventEmitterRegistryController(() => {
        pageRegistry.values().forEach((ctrl: any) => {
          if (!ctrl.onUpdateIds.includes('webview')) {
            ctrl.onUpdate((forceEmit: boolean) => {
              sendToReactEvent('ctrl.update', {
                ctrlName: ctrl.name,
                state: ctrl.toJSON(),
                forceEmit
              })
            }, 'webview')
          }
          if (!ctrl.onErrorIds.includes('webview')) {
            ctrl.onError(() => {
              sendToReactEvent('ctrl.error', { ctrlName: ctrl.name, errors: ctrl.emittedErrors })
            }, 'webview')
          }
        })
      })
      pageRegistryRef.current = pageRegistry

      const worker = new Worker(
        new URL('../../../mobile-web/services/mainWorker/mainController.worker.ts', import.meta.url)
      )
      workerRef.current = worker

      // Auto-lock runs on the page (DOM + railgunEmitter activity sources) and
      // triggers the keystore wipe by messaging the worker. Must be retryable —
      // posting the message never throws, so the timer loop survives.
      autoLockCtrlRef.current = new AutoLockController(pageRegistry, () => {
        worker.postMessage({ type: 'lock' })
      })

      return new Promise<string[]>((resolve, reject) => {
        worker.onmessage = (e: MessageEvent) => {
          const msg = e.data || {}
          switch (msg.type) {
            case 'reactEvent':
              // Worker serialized the payload with richJson (see worker's
              // sendToReactEvent) to survive structured-clone + carry BigInt.
              sendToReactEvent(msg.eventType, parse(msg.payloadJson))
              break
            case 'hwTrezorInvoke':
              // Worker asked the page-side Trezor SDK to run a device call.
              handleHwTrezorInvoke(worker, msg)
              break
            case 'hwLatticeInvoke':
              // Worker asked the page-side gridplus-sdk client to run a device call.
              handleHwLatticeInvoke(worker, msg)
              break
            case 'hwLedgerInvoke':
              // Worker asked the page-side WebHID Ledger controller to run a device call.
              handleHwLedgerInvoke(worker, msg)
              break
            case 'railgunEvent':
              // A1 milestone 1: the nested railgun worker's engine events,
              // relayed by the keystore worker. Replay onto the in-page
              // `railgunEmitter` so existing UI listeners and the AutoLock
              // scan-activity bridge keep working unchanged.
              emitRailgunEvent(msg.eventName as RailgunEventName, parse(msg.payloadJson))
              break
            case 'railgunWorkerStatus':
              if (msg.status === 'ready') setRailgunWorkerEngineReady()
              else if (msg.status === 'error')
                setRailgunWorkerEngineError(msg.message || 'railgun worker engine error')
              break
            case 'workerLog': {
              // Debug-only (FEATURES.railgunWorkerLogForwarding): the nested railgun
              // worker's console line, relayed up through the keystore worker. Print
              // it on the page so it shows in plain DevTools without CDP auto-attach.
              const fn = (console as any)[msg.level] || console.log
              // eslint-disable-next-line no-console
              fn('[railgun.worker]', msg.text)
              break
            }
            case 'railgunWalletResult':
              // A1 milestone 2: public wallet identity relayed from the keystore
              // worker (no seed/encryptionKey). Resolve the matching page request.
              resolveRailgunWalletResult(msg.requestId, { info: msg.info, error: msg.error })
              break
            case 'dappProviderResult':
              // In-app dApp browser: an EIP-1193 request result from the worker
              // provider bridge. useDappProviderBridge correlates by requestId and
              // forwards it into the iframe. (No BigInt here, but stay consistent
              // with richJson + parse just in case a result carries one.)
              eventBus.emit('dappProviderResult', {
                requestId: msg.requestId,
                ok: msg.ok,
                result: msg.resultJson !== undefined ? parse(msg.resultJson) : undefined,
                error: msg.error
              })
              break
            case 'dappEvent':
              // dApp session event (accountsChanged / chainChanged / disconnect …)
              // broadcast by the worker; routed to the matching iframe by origin.
              eventBus.emit('dappProviderEvent', {
                sessionId: msg.sessionId,
                ...parse(msg.payloadJson)
              })
              break
            case 'wcEvent':
              // WalletConnect wallet-role lifecycle (connected / disconnected /
              // error) from the worker bridge; surfaced to useWalletConnect.
              eventBus.emit('walletConnectEvent', { event: msg.event, data: msg.data })
              break
            case 'ready': {
              workerReadyRef.current = true
              // Flush anything dispatched before the worker booted.
              const buffered = pendingRef.current
              pendingRef.current = []
              buffered.forEach((a) => worker.postMessage({ type: 'dispatch', action: a }))
              const names = [...(msg.names || []), PAGE_CTRL]
              // eslint-disable-next-line no-console
              console.log('[WebViewWorker.web] worker ready, controllers=', names)
              resolve(names)
              break
            }
            case 'initError':
              // eslint-disable-next-line no-console
              console.error('[WebViewWorker.web] worker init FAILED', msg.message, msg.stack)
              reject(new Error(msg.message || 'mainController.worker init failed'))
              break
            default:
              break
          }
        }
        worker.onerror = (err) => {
          // eslint-disable-next-line no-console
          console.error('[WebViewWorker.web] worker error', err.message)
          if (!workerReadyRef.current) reject(new Error(err.message || 'mainController.worker error'))
        }
        worker.postMessage({ type: 'init', config })
      })
    }
  }))

  return null
})
